/** @format */

/* eslint @typescript-eslint/prefer-string-starts-ends-with: "warn" -- Legacy code: do not use as an example. */
import { merge } from 'lodash';
import { ParsedUrlQuery } from 'querystring';

import { toSnakeCase } from '@toucan/common/helpers/change-case';
import {
    ProductConditionStarRating,
    SearchFacet,
} from '@toucan/common/services/search/constants';

import { toCamelCase } from '@toucan/client/global/axios';
import {
    ContentLanguageFallbacks,
    MarketCode,
    MarketIso,
    defaultMarket,
} from '@toucan/client/global/market';
import { ProductConditionValue } from '@toucan/client/global/product';
import { safeDecodeURIComponent } from '@toucan/client/global/url';

import {
    Query,
    SearchListingModel,
    SelectedFilters,
    SelectedVariants,
} from './types';

export function getContentLanguage(locale: MarketIso): string {
    const isoWithFallbacks = Object.keys(ContentLanguageFallbacks);

    if (isoWithFallbacks.includes(locale)) {
        // @ts-ignore - Element implicitly has an any type - Touch and Change
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Legacy code: do not use as an example.
        return ContentLanguageFallbacks[locale];
    }

    return locale;
}
/**
 * Escape Solr query
 *
 * This escapes the '-' and standard brackets characters so that it is considered literally
 * and not as an _excludes_ or grouped search.
 */

export function escapeSolrQuery(query?: string): string | undefined {
    if (!query) {
        return;
    }
    // '\' is escape character in js, so '\\' is a literal '\' string
    // escape hyphens with double slash
    let result = query.replace(/(^|\W)(-)\b/g, '$1\\$2');

    // escape brackets with double slash
    result = result.replace(/(\(|\))/g, '\\$1');

    return result;
}
/**
 * Transforms filter values into a format Solr can recognise
 *
 * @param key
 * @param value
 */

export function transformToSearchValues(
    key: string,
    value: string | string[] | number | boolean
): string | string[] | number | boolean {
    if ((key as SearchFacet) === SearchFacet.productPriceModifier) {
        if (Array.isArray(value)) {
            return `(${value.join(' AND ')})`;
        }

        return value;
    }

    // Known numerical facets that don't conform to a predictable naming
    // convention
    const knownNumericFacets = ['productPrice'];
    const solrSpecialCharactersRegex =
        /(?=\+|&&|\|{2}|!|\(|\)|{|}|\^|~|\?|\/)/g;

    if (Array.isArray(value)) {
        return value.map((value) =>
            transformToSearchValues(key, value)
        ) as string[];
    }

    // Prepare any numerical facet value
    if (knownNumericFacets.includes(key) || /Num$/.test(key)) {
        const stringValue = String(value);

        if (stringValue.includes('-')) {
            const statement = /-$/.test(stringValue) ? ' TO *' : ' TO ';
            const rangeQuery = stringValue.replace('-', statement);
            return `[${rangeQuery}]`;
        }

        return value;
    }

    // We need to wrap any filter option values in double-quotes so that
    // Solr interprets them as literal values instead of trying to handle
    // special characters
    // https://solr.apache.org/guide/8_4/the-standard-query-parser.html#escaping-special-characters
    if (typeof value === 'string') {
        return value.replace(solrSpecialCharactersRegex, '\\');
    }

    return value;
}
export function getProductConditionStarRating(
    condition: ProductConditionValue
) {
    return {
        [ProductConditionValue.UNKNOWN]: null,
        [ProductConditionValue.FAULTY]: ProductConditionStarRating.ZERO,
        [ProductConditionValue.SR]: ProductConditionStarRating.ZERO,
        [ProductConditionValue['S/R']]: ProductConditionStarRating.ZERO,
        [ProductConditionValue.HEAVILY_USED]: ProductConditionStarRating.ONE,
        [ProductConditionValue['HEAVILY-USED']]: ProductConditionStarRating.ONE,
        [ProductConditionValue.WELL_USED]: ProductConditionStarRating.TWO,
        [ProductConditionValue['WELL-USED']]: ProductConditionStarRating.TWO,
        [ProductConditionValue.GOOD]: ProductConditionStarRating.THREE,
        [ProductConditionValue.EXCELLENT]: ProductConditionStarRating.FOUR,
        [ProductConditionValue.LIKE_NEW]: ProductConditionStarRating.FIVE,
        [ProductConditionValue['LIKE-NEW']]: ProductConditionStarRating.FIVE,
        [ProductConditionValue.NEW]: ProductConditionStarRating.SIX,
    }[condition];
}

export function getSearchParamKey(filterId: string) {
    const snakeCaseFilterId = toSnakeCase(filterId);

    return `filter_${snakeCaseFilterId}`;
}

function getVariantsFromQuery(query: ParsedUrlQuery): SelectedVariants {
    const variants = {} as SelectedVariants;

    Object.entries(query).forEach(([key, value]) => {
        if (key.startsWith('variant_') && value) {
            const variantKey = toCamelCase<SearchFacet>(
                key.replace('variant_', '')
            );

            variants[variantKey] = value;
        }
    });

    return variants;
}

function getFiltersFromQuery(query: ParsedUrlQuery): SelectedFilters {
    const filters = {} as SelectedFilters;

    Object.entries(query).forEach(([key, value]) => {
        if (key.startsWith('filter_') && value) {
            const filterKey = toCamelCase<SearchFacet>(
                key.replace('filter_', '')
            );

            filters[filterKey] = value;
        }
    });

    return filters;
}

function getSortFromQuery(query: ParsedUrlQuery) {
    if (!query.sort || typeof query.sort !== 'string') {
        return {};
    }

    const [facet, direction] = query.sort.split(':');
    return { [facet]: direction };
}

export function getSearchParamsFromQuery(query: ParsedUrlQuery) {
    const page = query.page ? parseInt(query.page.toString()) : 1;
    const pageIndex = page - 1;

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const showAll = Boolean(query.showAll);

    const originalQuery =
        query.originalQuery?.toString().replace(/[<>]/g, '') ?? '';
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const outOfStock = Boolean(query.outOfStock);
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const noInStockResults = Boolean(query.noInStockResults);

    const variants = getVariantsFromQuery(query);
    const selectedFilters = getFiltersFromQuery(query);
    const sort = getSortFromQuery(query);

    return {
        pageIndex,
        showAll,
        variants,
        selectedFilters,
        sort,
        originalQuery,
        outOfStock,
        noInStockResults,
    };
}

export function getSearchTermFromQuery(query: ParsedUrlQuery): string {
    return safeDecodeURIComponent(query.q?.toString() ?? '').replace(
        /[<>]/g,
        ''
    );
}

export function hasOneProductInResult(results: SearchListingModel[]) {
    return (
        results.length === 1 &&
        parseInt(results[0].productPriceCount?.values?.[0] ?? '') === 1
    );
}

export function getProductSKUQuery(
    baseQuery: Query,
    modelId: string,
    market: MarketCode = defaultMarket.code,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
    showOutOfStock: boolean = false
): Query {
    const isSKUSearch = !isNaN(Number(baseQuery.query));

    // If its a SKU search, we need to add the query filter
    // otherwise it can return multiple models and pass back the incorrect item.
    // If not, then we need to remove the query, as it could be a semantic query
    // which would only show a single item if all other times have been filtered out
    const query = isSKUSearch ? baseQuery.query : '';

    return merge({}, baseQuery, {
        query,
        fieldList: ['product_sku'],
        filterQuery: {
            modelMarket: market,
            objectType: 'product',
            modelAvailable: !showOutOfStock,
            modelId,
        },
        groupField: null,
        start: 0,
        rows: 1,
    });
}
